import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { trackOrder } from '../marketApi';
import { formatPrice } from '../currency';

function statusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return 'badge-success';
  if (s === 'cancelled') return 'badge-error';
  if (s === 'confirmed') return 'badge-blue';
  if (s === 'packed') return 'badge-purple';
  return 'badge-warning';
}

function StatusTimeline({ status }) {
  const steps = ['pending', 'confirmed', 'packed', 'delivered'];
  const current = steps.indexOf((status || '').toLowerCase());
  const cancelled = (status || '').toLowerCase() === 'cancelled';

  if (cancelled) {
    return (
      <div
        style={{
          padding: '12px 16px',
          background: 'var(--error-bg)',
          borderRadius: 8,
          color: 'var(--error)',
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        Order Cancelled
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, margin: '20px 0' }}>
      {steps.map((step, i) => {
        const done = i <= current;
        const isLast = i === steps.length - 1;
        return (
          <React.Fragment key={step}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: done ? 'var(--primary)' : 'var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: done ? '#fff' : 'var(--muted)',
                  fontWeight: 700,
                  fontSize: 14,
                  transition: 'background 0.2s',
                }}
              >
                {done ? '✓' : i + 1}
              </div>
              <div
                className="mk-timeline-label"
                style={{
                  fontSize: 11,
                  color: done ? 'var(--primary)' : 'var(--muted)',
                  fontWeight: done ? 600 : 400,
                  marginTop: 6,
                  textTransform: 'capitalize',
                }}
              >
                {step}
              </div>
            </div>
            {!isLast && (
              <div
                style={{
                  height: 2,
                  flex: 2,
                  background: i < current ? 'var(--primary)' : 'var(--border)',
                  marginBottom: 20,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function groupBySeller(items) {
  const map = new Map();
  for (const item of (items || [])) {
    const key = String(item.seller_id || item.sellerId || 'unknown');
    const name = item.seller_name || item.sellerName || 'Seller';
    if (!map.has(key)) map.set(key, { sellerName: name, items: [], subtotal: 0 });
    const g = map.get(key);
    g.items.push(item);
    g.subtotal += (item.price || 0) * (item.quantity || 1);
  }
  return Array.from(map.values());
}

export default function TrackOrder() {
  const [ref, setRef] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleTrack(e) {
    e.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const data = await trackOrder(ref.trim());
      setOrder(data);
    } catch (e) {
      setError(e.message || 'Order not found. Please check the reference number.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-container" style={{ maxWidth: 640, margin: '0 auto' }}>
      <h2 className="section-title" style={{ marginTop: 8 }}>Track Your Order</h2>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <form onSubmit={handleTrack}>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Order Reference Number</label>
            <input
              className="form-control"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="e.g. SMB-1234567890"
              style={{ fontFamily: 'monospace', fontSize: 15 }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading || !ref.trim()}
          >
            {loading ? 'Tracking...' : 'Track Order'}
          </button>
        </form>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {order && (
        <div className="card" style={{ padding: 20 }}>
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 8,
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                Order {order.order_ref || order.orderRef || order.ref || ref}
              </div>
              {(order.created_at || order.createdAt) && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  Placed on {new Date(order.created_at || order.createdAt).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </div>
              )}
            </div>
            <span
              className={`badge ${statusBadgeClass(order.status)}`}
              style={{ fontSize: 13, padding: '4px 12px' }}
            >
              {(order.status || 'pending').toUpperCase()}
            </span>
          </div>

          {/* Timeline */}
          <StatusTimeline status={order.status} />

          <hr className="divider" />

          {/* Buyer details */}
          <div style={{ marginBottom: 16, fontSize: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
              DELIVERY DETAILS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div>
                <span style={{ color: 'var(--muted)', fontSize: 12, minWidth: 60, display: 'inline-block' }}>Name</span>
                <strong>{order.buyer_name || order.buyerName}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--muted)', fontSize: 12, minWidth: 60, display: 'inline-block' }}>Phone</span>
                <strong>{order.buyer_phone || order.phone}</strong>
              </div>
              <div>
                <span style={{ color: 'var(--muted)', fontSize: 12, minWidth: 60, display: 'inline-block' }}>Address</span>
                <strong>{order.delivery_address || order.deliveryAddress}</strong>
              </div>
              {order.city && (
                <div>
                  <span style={{ color: 'var(--muted)', fontSize: 12, minWidth: 60, display: 'inline-block' }}>City</span>
                  <strong>{order.city}</strong>
                </div>
              )}
            </div>
          </div>

          <hr className="divider" />

          {/* Items grouped by seller */}
          {order.items && order.items.length > 0 && (() => {
            const groups = groupBySeller(order.items);
            const multi = groups.length > 1;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
                  ITEMS ORDERED
                </div>
                {multi && (
                  <div style={{
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13,
                  }}>
                    <span style={{ fontWeight: 700, color: '#92400e' }}>🏪 {groups.length} sellers</span>
                    <span style={{ color: '#78350f' }}>
                      {' '}— items are being fulfilled by different sellers. Each seller delivers their products separately.
                    </span>
                  </div>
                )}
                {groups.map((g, gi) => (
                  <div key={gi} style={{ marginBottom: multi ? 16 : 0 }}>
                    {multi && (
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        padding: '6px 0', borderBottom: '2px solid var(--primary)',
                        marginBottom: 8,
                      }}>
                        🏪 {g.sellerName}
                      </div>
                    )}
                    {g.items.map((item, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 14,
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {item.product_name || item.productName || item.name || `Product #${item.product_id || item.productId}`}
                          </div>
                          <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                            {formatPrice(item.price, item.currency)} × {item.quantity}
                          </div>
                        </div>
                        <div style={{ fontWeight: 700 }}>
                          {formatPrice((item.price || 0) * (item.quantity || 1), item.currency)}
                        </div>
                      </div>
                    ))}
                    {multi && (
                      <div style={{
                        display: 'flex', justifyContent: 'flex-end',
                        fontSize: 13, color: 'var(--muted)', padding: '6px 0',
                      }}>
                        Subtotal: <strong style={{ marginLeft: 6 }}>{formatPrice(g.subtotal, g.items[0]?.currency || 'INR')}</strong>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Total */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 800,
              fontSize: 18,
              marginBottom: 8,
            }}
          >
            <span>Total</span>
            <span>{formatPrice(order.total_amount || order.total || order.totalAmount || 0, (order.items?.[0]?.currency) || order.currency || 'INR')}</span>
          </div>

          {order.notes && (
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>
              Note: {order.notes}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <Link to="/" style={{ color: 'var(--muted)', fontSize: 13 }}>
          ← Back to Home
        </Link>
      </div>
    </div>
  );
}
